/**
 * Поля дати й часу — одні на весь застосунок.
 *
 * Це кнопка з датою, а не поле введення: календар усе одно потрібен, а два
 * способи задати те саме (набрати число й обрати день) плодили рядки різної
 * висоти й вигляду. Кнопка стоїть рівно там, де стояло б поле, і має ті самі
 * межі й висоту, що поля поруч.
 *
 * Сам календар малюється поверх сторінки, а не всередині картки: картки з
 * полями прокручуються власним стовпцем і обрізали б усе, що з них вилазить,
 * а місяць вищий за поле в рази.
 */

import {
  addDays,
  isWeekend,
  monthDates,
  monthTitle,
  todayIso,
  weekdayHeaders,
  weekIndex,
  type IsoDate,
} from '@kancelaria/shared';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useData } from '../store/data.js';

/** «16.09.2026» — так дата стоїть на кнопці. */
const human = (d: IsoDate | ''): string =>
  d ? `${d.slice(8)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : '';

/** Місяць, який показати першим: обрана дата, а без неї — сьогоднішній. */
const monthOf = (d: string): string => (d ? d.slice(0, 7) : todayIso().slice(0, 7));

const shiftMonth = (ym: string, by: number): string => {
  const d = new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1 + by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const CAL_W = 268;

/**
 * Підкладка під полем: календар чи години. Малюється поверх сторінки, сама
 * стає під полем або над ним і закривається кліком повз, Esc або прокруткою —
 * бо тоді вона вже не біля свого поля.
 */
function Popover({
  anchor,
  width,
  className,
  onClose,
  children,
}: {
  anchor: React.RefObject<HTMLElement | null>;
  width: number;
  className: string;
  onClose: () => void;
  children: ReactNode;
}): ReactNode {
  const [at, setAt] = useState({ top: -9999, left: 0 });
  const pop = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const r = anchor.current?.getBoundingClientRect();
    if (!r) return;
    const h = pop.current?.offsetHeight ?? 320;
    const below = window.innerHeight - r.bottom;
    const preferredTop = below > h + 12 || r.top < h + 12 ? r.bottom + 6 : r.top - h - 6;
    const next = {
      // У низькому вікні календар може не вміститися ні над полем, ні під ним.
      top: Math.max(8, Math.min(preferredTop, window.innerHeight - h - 8)),
      left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
    };
    // Порівнюємо перед записом: інакше вимір щоразу перемальовував би себе.
    setAt(a => (a.top === next.top && a.left === next.left ? a : next));
  });

  useEffect(() => {
    const away = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (pop.current?.contains(t) || anchor.current?.contains(t)) return;
      onClose();
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    const scroll = (e: Event): void => {
      if (e.target instanceof Node && pop.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', scroll, true);
      window.removeEventListener('resize', onClose);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div
      ref={pop}
      className={className}
      role="dialog"
      style={{
        top: at.top,
        left: at.left,
        width,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: 'calc(100dvh - 16px)',
        overflowY: 'auto',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** Календар одного місяця, прив'язаний до поля. */
function Calendar({
  anchor,
  value,
  clearable,
  onPick,
  onClear,
  onClose,
}: {
  anchor: React.RefObject<HTMLElement | null>;
  value: IsoDate | '';
  clearable?: boolean;
  onPick: (d: IsoDate) => void;
  onClear: () => void;
  onClose: () => void;
}): ReactNode {
  const weekStart = useData(s => (s.session?.org.settings.weekStart ?? 1) as 0 | 1);
  const locale = useData(s => s.session?.org.settings.locale ?? 'uk');
  const [month, setMonth] = useState(() => monthOf(value));

  const y = Number(month.slice(0, 4));
  const m0 = Number(month.slice(5, 7)) - 1;
  const days = monthDates(y, m0);
  const blanks = weekIndex(days[0]!, weekStart);
  const today = todayIso();

  return (
    <Popover anchor={anchor} width={CAL_W} className="cal" onClose={onClose}>
      <div className="cal-head">
        <button type="button" className="btn sm cal-step" onClick={() => setMonth(m => shiftMonth(m, -1))}>
          ‹
        </button>
        <span>{monthTitle(y, m0, locale)}</span>
        <button type="button" className="btn sm cal-step" onClick={() => setMonth(m => shiftMonth(m, 1))}>
          ›
        </button>
      </div>
      <div className="cal-week">
        {weekdayHeaders(weekStart, locale).map((w, i) => (
          <span key={i}>{w}</span>
        ))}
      </div>
      <div className="cal-grid">
        {Array.from({ length: blanks }, (_, i) => (
          <span key={`b${i}`} />
        ))}
        {days.map(d => (
          <button
            key={d}
            type="button"
            className={['cal-day', isWeekend(d) ? 'off' : '', d === today ? 'now' : '', d === value ? 'on' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onPick(d)}
          >
            {Number(d.slice(8))}
          </button>
        ))}
      </div>
      <div className="cal-foot">
        <button type="button" className="btn sm" onClick={() => onPick(today)}>
          Сьогодні
        </button>
        <button type="button" className="btn sm" onClick={() => onPick(addDays(today, 1))}>
          Завтра
        </button>
        {clearable ? (
          <button type="button" className="btn sm" onClick={onClear}>
            Очистити
          </button>
        ) : null}
      </div>
    </Popover>
  );
}

export function DateInput({
  value,
  onChange,
  base,
  clearable,
  empty = 'обрати дату',
  disabled,
}: {
  value: IsoDate | '';
  onChange: (d: IsoDate | '') => void;
  /** Місяць, який відкрити, поки дати немає. */
  base?: IsoDate | '';
  /** Порожнє значення дозволене — у календарі з'являється «Очистити». */
  clearable?: boolean;
  /** Що написано на кнопці, поки дати немає. */
  empty?: string;
  disabled?: boolean;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        ref={box}
        type="button"
        className={`datebtn${open ? ' on' : ''}${value ? '' : ' blank'}`}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
      >
        <span>{value ? human(value) : empty}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
      </button>
      {open ? (
        <Calendar
          anchor={box}
          value={value || (base ?? '')}
          clearable={clearable}
          onPick={d => {
            onChange(d);
            setOpen(false);
          }}
          onClear={() => {
            onChange('');
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

/**
 * Період — двома такими самими полями. Дати, що розійшлися, поле поправляє
 * саме: кінець раніше за початок означає, що рухали початок.
 */
export function PeriodInput({
  value,
  onChange,
  disabled,
}: {
  value: { from: IsoDate; to: IsoDate };
  onChange: (p: { from: IsoDate; to: IsoDate }) => void;
  disabled?: boolean;
}): ReactNode {
  return (
    <div className="period-row">
      <DateInput
        value={value.from}
        base={value.to}
        disabled={disabled}
        onChange={d => d && onChange({ from: d, to: d > value.to ? d : value.to })}
      />
      <span className="period-dash">–</span>
      <DateInput
        value={value.to}
        base={value.from}
        disabled={disabled}
        onChange={d => d && onChange({ from: d < value.from ? d : value.from, to: d })}
      />
    </div>
  );
}

/* --------------------------------------------------------------- час */

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
/** Чверті години: у розпорядку дня чверть — найдрібніша одиниця, що трапляється. */
const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

const TIME_W = 300;

/**
 * Поле часу. Таке саме, як поле дати: кнопка зі значенням, а вибір —
 * підкладкою під нею. Години й хвилини стоять сітками: клік по годині вже дає
 * час, клік по хвилині його уточнює й закриває вибір.
 */
export function TimeInput({
  value,
  onChange,
  clearable,
  empty = 'обрати час',
  disabled,
}: {
  value: string;
  onChange: (t: string) => void;
  /** Порожнє значення дозволене — у вибірці з'являється «Очистити». */
  clearable?: boolean;
  /** Що написано на кнопці, поки часу немає. */
  empty?: string;
  disabled?: boolean;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLButtonElement>(null);
  const hour = value.slice(0, 2);
  const minute = value.slice(3, 5);

  return (
    <>
      <button
        ref={box}
        type="button"
        className={`datebtn${open ? ' on' : ''}${value ? '' : ' blank'}`}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
      >
        <span>{value || empty}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      </button>
      {open ? (
        <Popover anchor={box} width={TIME_W} className="timepop" onClose={() => setOpen(false)}>
          <div className="timepop-cols">
            <div className="timepop-col">
              <span className="timepop-title">Година</span>
              <div className="timepop-grid h">
                {HOURS.map(h => (
                  <button
                    key={h}
                    type="button"
                    className={`timepop-cell${h === hour ? ' on' : ''}`}
                    onClick={() => onChange(`${h}:${minute || '00'}`)}
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>
            <div className="timepop-col">
              <span className="timepop-title">Хвилини</span>
              <div className="timepop-grid m">
                {MINUTES.map(m => (
                  <button
                    key={m}
                    type="button"
                    className={`timepop-cell${m === minute ? ' on' : ''}`}
                    onClick={() => {
                      onChange(`${hour || '00'}:${m}`);
                      setOpen(false);
                    }}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {clearable ? (
            <div className="cal-foot">
              <button
                type="button"
                className="btn sm"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
              >
                Очистити
              </button>
            </div>
          ) : null}
        </Popover>
      ) : null}
    </>
  );
}
